import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stethoscope, ArrowLeft, CheckCircle, Calendar, Users, TrendingUp, Zap, Video, Target } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ServicesPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();

  const packages = [
    {
      name: 'Content Starter',
      price: '$299',
      period: 'per month',
      description: 'Perfect for getting started with consistent content',
      features: [
        '4 video scripts per month',
        'AI content ideas generation',
        'Compliance checking',
        'Caption & hashtag generation',
        'Email support'
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      name: 'Growth Package',
      price: '$599',
      period: 'per month',
      description: 'For doctors serious about building their online presence',
      features: [
        '8 video scripts per month',
        'Professional video editing',
        'Monthly content calendar',
        'Social media posting',
        'Basic analytics',
        'Priority support'
      ],
      cta: 'Most Popular',
      popular: true
    },
    {
      name: 'Full Service',
      price: '$1,299',
      period: 'per month',
      description: 'Complete hands-free content management',
      features: [
        'Unlimited video scripts',
        'AI avatar videos (coming soon)',
        'Full content management',
        'Multi-platform distribution',
        'Advanced analytics & reporting',
        'Lead generation strategy',
        'Dedicated account manager',
        '24/7 priority support'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  const services = [
    {
      icon: Calendar,
      title: 'Content Calendar Planning',
      description: 'Strategic monthly content calendar aligned with your practice goals'
    },
    {
      icon: Video,
      title: 'Professional Editing',
      description: 'High-quality video editing with subtitles, b-roll, and branding'
    },
    {
      icon: Users,
      title: 'Social Media Management',
      description: 'Complete management of your Instagram, YouTube, and LinkedIn'
    },
    {
      icon: Target,
      title: 'Lead Generation',
      description: 'Convert viewers into patients with proven strategies'
    },
    {
      icon: TrendingUp,
      title: 'Growth Analytics',
      description: 'Track your growth with detailed performance reports'
    },
    {
      icon: Zap,
      title: 'Quick Turnaround',
      description: '24-48 hour delivery for all content pieces'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(`/dashboard/${doctorId}`)}
              variant="ghost"
              size="sm"
              className="rounded-full"
              data-testid="services-back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Stethoscope className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            <span className="text-2xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Managed Services
            </span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1C2A21] mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Want Us to Handle Everything?
          </h1>
          <p className="text-xl text-[#5C7063] max-w-3xl mx-auto">
            Focus on patient care while we build your digital presence. 
            Our team creates, edits, and distributes content on your behalf.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10"
            >
              <div className="w-14 h-14 bg-[#82A08B]/20 rounded-2xl flex items-center justify-center mb-4">
                <service.icon className="w-7 h-7 text-[#2C5F44]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-[#1C2A21] mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
                {service.title}
              </h3>
              <p className="text-[#5C7063]">{service.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Pricing Packages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-bold text-[#1C2A21] text-center mb-12" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Choose Your Package
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                className={`bg-white rounded-3xl p-8 border-2 relative ${
                  pkg.popular ? 'border-[#2C5F44] shadow-[0_8px_30px_rgba(44,95,68,0.12)]' : 'border-[#2C5F44]/10'
                }`}
                data-testid={`service-package-${index}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="inline-block px-4 py-1 bg-[#2C5F44] text-white rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <h3 className="text-2xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {pkg.name}
                </h3>
                <p className="text-[#5C7063] mb-6">{pkg.description}</p>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    {pkg.price}
                  </span>
                  <span className="text-[#5C7063]"> {pkg.period}</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-[#2C5F44] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-[#1C2A21]">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`button-pill w-full rounded-full ${
                    pkg.popular
                      ? 'bg-[#2C5F44] hover:bg-[#244b36] text-white'
                      : 'bg-white border-2 border-[#2C5F44]/20 hover:bg-[#F4F1EA] text-[#2C5F44]'
                  }`}
                  data-testid={`service-cta-btn-${index}`}
                >
                  {pkg.cta}
                </Button>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="bg-gradient-to-br from-[#2C5F44] to-[#244b36] rounded-3xl p-12 text-center text-white"
        >
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Ready to Scale Your Practice?
          </h2>
          <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
            Book a free consultation to discuss your content goals and find the perfect package for your practice.
          </p>
          <Button
            className="button-pill bg-white hover:bg-gray-100 text-[#2C5F44] rounded-full px-8 py-6 text-lg font-semibold"
            data-testid="services-book-consultation-btn"
          >
            Book Free Consultation
          </Button>
        </motion.div>
      </section>
    </div>
  );
}